import classNames from "classnames";
import { Popper, PopperProps } from "@remo-co/ui-core/src/components/Popper";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Input } from "@remo-co/ui-core/src/components/Input";
import { Autocomplete } from "@remo-co/ui-core/src/components/Autocomplete";
import {
  createFilterOptions,
  FilterOptionsState,
} from "@remo-co/ui-core/src/hooks/useAutocomplete";
import { Add } from "@remo-co/ui-core/src/icons/Add";
import { Delete } from "@remo-co/ui-core/src/icons/Delete";
import { DragIndicator } from "@remo-co/ui-core/src/icons/DragIndicator";
import { Draggable, Container as DraggableContainer } from "react-smooth-dnd";
import { useI18n } from "i18n";
import { IEvent } from "modules/event/types";
import { useUserSplitTreatment } from "modules/split";
import { UserFeatures } from "services/splitService/features";
import { IEventDirectory } from "modules/eventDirectory/types";
import EventListOption from "../EventListOption";
import { useStyles } from "./styles";
import { EventOption } from "../EventDirectoryDialog";
import { LinkedLockedEventsInput } from "./LinkedLockedEventsInput";

interface IDraggableResult {
  removedIndex: number | null;
  addedIndex: number | null;
}

interface IEventListProps {
  eventArray: EventOption[];
  reducedEventArray: string[];
  options: IEvent[];
  setEventArray: (array: EventOption[]) => void;
  touched: boolean;
  minEvents: number;
  maxEvents: number;
  directory?: IEventDirectory;
}

export const EventList = ({
  eventArray,
  reducedEventArray,
  setEventArray,
  options,
  touched,
  minEvents,
  maxEvents,
  directory,
}: IEventListProps) => {
  const styles = useStyles();
  const { t } = useI18n(["eventDirectory"]);
  const dragHandleSelectorClassname = ".column-drag-handle";
  const defaultFilterOptions = createFilterOptions<IEvent>();
  const { featureEnabled } = useUserSplitTreatment(
    UserFeatures.MULTIPLE_EVENT_DIRECTORIES,
  );

  // this removes all events already selected from the list
  const filterOptions = (
    storedOptions: IEvent[],
    state: FilterOptionsState<IEvent>,
  ) =>
    defaultFilterOptions(
      storedOptions.filter((option) =>
        !reducedEventArray.includes(option.id)
          ? featureEnabled ||
            (!option.directoryId &&
              !option?.directories?.length &&
              !option.isPrivate)
          : false,
      ),
      state,
    );

  const handleOptionChange = (index: number, value: EventOption) => {
    const newEventArray = [...eventArray];

    newEventArray[index] = value;
    setEventArray(newEventArray);
  };
  const addEntry = () => {
    setEventArray([...eventArray, null]);
  };
  const deleteEntry = (index: number) => {
    const newEventArray = [...eventArray];

    newEventArray.splice(index, 1);
    setEventArray(newEventArray);
  };
  const handleOnDrop = ({ removedIndex, addedIndex }: IDraggableResult) => {
    const newEventArray = [...eventArray];
    let itemToAdd = null;

    if (removedIndex !== null) {
      [itemToAdd] = newEventArray.splice(removedIndex, 1);
    }

    if (addedIndex !== null) {
      newEventArray.splice(addedIndex, 0, itemToAdd);
    }
    setEventArray(newEventArray);
  };

  const getEventDirectoriesName = (directories?: IEventDirectory[]) => {
    if (directories) {
      return directories.map((ele) => ele.name);
    }
    return [];
  };

  const checkSameDirectory = (directories: IEventDirectory[]) =>
    directories.length === 1 && directories[0].name === directory?.name;

  // eslint-disable-next-line react/no-unstable-nested-components
  const CustomPopper = (props: PopperProps) => (
    <Popper
      {...props}
      className={styles.draggableItemPopper}
      placement="bottom-start"
    />
  );

  return (
    <>
      {/* @ts-expect-error react 18 has removed children prop from FunctionComponent and react-smooth-dnd is not maintained */}
      <DraggableContainer
        lockAxis="y"
        dragHandleSelector={dragHandleSelectorClassname}
        onDrop={handleOnDrop}
      >
        {eventArray.map((eventId, index) => (
          // @ts-expect-error react 18 has removed children prop
          // eslint-disable-next-line react/no-array-index-key
          <Draggable key={`${index}-${eventId}`}>
            <Container
              fullWidth
              flex
              justifyContent="flex-start"
              className={styles.draggableItemContainer}
            >
              <DragIndicator
                className={classNames(
                  "column-drag-handle",
                  styles.draggableIndicator,
                )}
                data-testid="event-list-draggable-handle"
              />
              <Autocomplete
                id={`autocomplete-${index}`}
                freeSolo
                fullWidth
                data-testid="event-list-autocomplete"
                PopperComponent={CustomPopper}
                onChange={(_evt, value) => {
                  handleOptionChange(index, value as EventOption);
                }}
                value={eventId}
                filterOptions={filterOptions}
                options={options}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) =>
                  `${typeof option === "string" ? option : option.name}`
                }
                renderOption={(props, option) => (
                  <EventListOption
                    option={option}
                    data-testid="event-list-options"
                    {...props}
                  />
                )}
                renderInput={(params) =>
                  (eventArray[index]?.directories &&
                    eventArray[index]?.directories?.length &&
                    !checkSameDirectory(
                      eventArray[index]?.directories as IEventDirectory[],
                    )) ||
                  eventArray[index]?.isPrivate ? (
                    <LinkedLockedEventsInput
                      params={params}
                      error={
                        !!(touched && reducedEventArray.length < minEvents)
                      }
                      directories={getEventDirectoriesName(
                        eventArray[index]?.directories as IEventDirectory[],
                      )}
                      isPrivateEvent={eventArray[index]?.isPrivate}
                      isMultipleEventDirectories={Boolean(
                        eventArray[index]?.directories &&
                          eventArray[index]?.directories?.length &&
                          // @ts-expect-error type error
                          !checkSameDirectory(eventArray[index]?.directories),
                      )}
                    />
                  ) : (
                    <Container
                      ref={params.InputProps.ref}
                      className={styles.inputContainer}
                    >
                      <Input
                        {...params.inputProps}
                        inputProps={{ "data-testid": "event-list-input" }}
                        size="sm"
                        placeholder={t("eventDirectory:search.or.choose.event")}
                        fullWidth
                        inputEndAdornment={params.InputProps.endAdornment}
                        error={
                          !!(touched && reducedEventArray.length < minEvents)
                        }
                      />
                    </Container>
                  )
                }
              />
              {eventArray.length > minEvents && (
                <Button
                  color="dark"
                  size="sm"
                  variant="ghost"
                  isIconButton
                  data-testid="event-list-delete-button"
                  className={classNames(
                    styles.showOnHover,
                    styles.deleteEntryButton,
                  )}
                  onClick={() => deleteEntry(index)}
                >
                  <Delete fontSize="small" />
                </Button>
              )}
            </Container>
          </Draggable>
        ))}
      </DraggableContainer>
      {eventArray.length < maxEvents && (
        <Button
          color="blue"
          size="sm"
          variant="ghost"
          data-testid="event-list-add-button"
          className={styles.addEntryButton}
          contentClassName={styles.addEntryButtonContent}
          onClick={addEntry}
        >
          <Add fontSize="small" /> {t("eventDirectory:add.event")}
        </Button>
      )}
    </>
  );
};
